import { Checkbox } from '../../../widgets/checkbox';
import { InputDialog, QuestionDialog, useDialogManager } from '../../../widgets/dialog';
import { Local3DInfoDialog } from '../components/dialogs/local3DInfoDialog';
import { showErrorNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import { useDropdown } from '../../../widgets/hooks/useDropdown';
import { ZTooltip } from '../../../widgets/ztooltip';
import React, { FC, Suspense, useCallback, useEffect } from 'react';
import { useMutation } from 'react-relay';
import { stringFormat, T } from 'src/shared/helpers';
import styled from 'styled-components';
import { MoveToCategoryDialog } from '../components/results/moveToCategoryDialog';
import { ActionButton, DropdownDivider, DropdownMenuItem, ExpandButton } from '../components/shared/sharedStyles';
import { downloadFile } from '../utils/downloadHelpers';
import { assetsGrid_query$data } from './__generated__/assetsGrid_query.graphql';
import { assetLibraryQueriesDeleteAIAssetLibraryAssetMutation } from './__generated__/assetLibraryQueriesDeleteAIAssetLibraryAssetMutation.graphql';
import { assetLibraryQueriesUpdateAIAssetLibraryAssetMutation } from './__generated__/assetLibraryQueriesUpdateAIAssetLibraryAssetMutation.graphql';
import { AssetInfoDialog } from './assetInfoDialog';
import { deleteAIAssetLibraryAssetMutation, updateAIAssetLibraryAssetMutation } from './assetLibraryQueries';

type AssetNode = NonNullable<NonNullable<assetsGrid_query$data['aiAssetLibraryAssets']>['edges']>[number]['node'];

// Icons
import ArrowRightIcon from 'src/shared/icons/arrow-right-solid.svg';
import DownloadIcon from 'src/shared/icons/download-solid.svg';
import EditIcon from 'src/shared/icons/edit-solid.svg';
import EllipsisVIcon from 'src/shared/icons/ellipsis-v-solid.svg';
import ExpandIcon from 'src/shared/icons/expand-solid.svg';
import FolderIcon from 'src/shared/icons/folder-solid.svg';
import InfoCircleIcon from 'src/shared/icons/info-circle-solid.svg';
import PlayIcon from 'src/shared/icons/play-solid.svg';
import TrashIcon from 'src/shared/icons/trash-alt-solid.svg';
import VideoIcon from 'src/shared/icons/video-solid.svg';
import CubeIcon from 'src/shared/icons/cube-solid.svg';

const GlbViewer = React.lazy(() => import('../components/shared/glbViewer'));

function isModelAsset(asset: { assetType: string }): boolean {
    return asset.assetType === '3d-model';
}

const Card = styled.div`
    position: relative;
    border-radius: 8px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.backgroundColorBase};
    transition: all 0.2s;
    min-width: 0;
    overflow: hidden;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const AssetPreview = styled.div`
    aspect-ratio: 1;
    position: relative;
    background: #f5f5f5;
`;

const AssetImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const AssetVideo = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const VideoOverlay = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 4px;

    svg {
        width: 12px;
        height: 12px;
        fill: white;
    }
`;

const CheckboxWrapper = styled.div`
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;
`;

const MenuButtonWrapper = styled.div`
    position: absolute;
    bottom: 8px;
    left: 8px;
`;

const AssetInfo = styled.div`
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const AssetName = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${(props) => props.theme.app.textColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AssetMeta = styled.div`
    font-size: 11px;
    color: #78909c;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const CategoryBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    background: ${(props) => props.theme.app.primary_light};
    color: ${(props) => props.theme.app.primary};
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;

    svg {
        width: 10px;
        height: 10px;
        fill: currentColor;
    }
`;

interface AssetItemProps {
    asset: AssetNode;
    onAssetChange: () => void;
    /** Callback when user wants to use this asset as input for a tool */
    onUseAsInput?: (imageUrl: string) => void;
    /** Whether this asset is currently selected */
    isSelected?: boolean;
    /** Toggle selection callback */
    onToggleSelect?: (id: string) => void;
    /** Whether this item's menu is currently open (controlled by parent) */
    isMenuOpen?: boolean;
    /** Notify parent that this item's menu should open */
    onMenuOpen?: (id: string) => void;
    /** Notify parent that the menu should close */
    onMenuClose?: () => void;
    /** Callback to open the lightbox for this asset */
    onExpand?: () => void;
}

export const AssetItem: FC<AssetItemProps> = ({
    asset,
    onAssetChange,
    onUseAsInput,
    isSelected,
    onToggleSelect,
    isMenuOpen,
    onMenuOpen,
    onMenuClose,
    onExpand
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [setOpen, setClose, isOpen, Dropdown] = useDropdown();

    // Sync internal dropdown state with parent-controlled isMenuOpen
    useEffect(() => {
        if (!isMenuOpen && isOpen) {
            setClose();
        }
    }, [isMenuOpen, isOpen, setClose]);

    const [updateAsset] = useMutation<assetLibraryQueriesUpdateAIAssetLibraryAssetMutation>(updateAIAssetLibraryAssetMutation);
    const [deleteAsset] = useMutation<assetLibraryQueriesDeleteAIAssetLibraryAssetMutation>(deleteAIAssetLibraryAssetMutation);

    const handleDownload = useCallback(async () => {
        const extension = isModelAsset(asset) ? 'glb' : asset.assetType === 'video' ? 'mp4' : 'png';
        try {
            await downloadFile(asset.fileUrl, `ai-asset-${asset.name}.${extension}`);
        } catch (error) {
            console.error('Failed to download file:', error);
        }
    }, [asset]);

    const handleRename = useCallback(
        (newName: string) => {
            if (!newName.trim()) {
                showErrorNotification({ message: T._('Type a valid name', 'Admin') });
                return;
            }

            updateAsset({
                variables: {
                    id: asset.id,
                    name: newName.trim(),
                    categoryID: asset.categoryID ? parseInt(asset.categoryID.toString()) : null
                },
                onCompleted: () => {
                    showSuccessNotification({ message: T._('Asset renamed.', 'Admin') });
                    closeDialog('rename-asset');
                    onAssetChange();
                },
                onError: (error) => {
                    console.error('Failed to rename asset:', error);
                    showErrorNotification({ message: T._('Failed to rename asset', 'Admin') });
                }
            });
        },
        [asset, updateAsset, onAssetChange, closeDialog]
    );

    const handleMove = useCallback(
        (categoryId: number | null) => {
            updateAsset({
                variables: {
                    id: asset.id,
                    name: asset.name,
                    categoryID: categoryId
                },
                onCompleted: () => {
                    showSuccessNotification({ message: T._('Asset moved.', 'Admin') });
                    closeDialog('move-asset');
                    onAssetChange();
                },
                onError: (error) => {
                    console.error('Failed to move asset:', error);
                    showErrorNotification({ message: T._('Failed to move asset', 'Admin') });
                },
                updater: (store) => {
                    store.invalidateStore();
                }
            });
        },
        [asset, updateAsset, onAssetChange, closeDialog]
    );

    const handleDelete = useCallback(() => {
        deleteAsset({
            variables: {
                id: asset.id
            },
            onCompleted: () => {
                showSuccessNotification({ message: T._('Asset deleted.', 'Admin') });
                closeDialog('delete-asset');
                onAssetChange();
            },
            onError: (error) => {
                console.error('Failed to delete asset:', error);
                showErrorNotification({ message: T._('Failed to delete asset', 'Admin') });
            },
            updater: (store) => {
                store.invalidateStore();
            }
        });
    }, [asset, deleteAsset, onAssetChange, closeDialog]);

    const openRenameDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            showDialog('rename-asset', <InputDialog label={T._('Asset Name', 'Admin')} defaultValue={asset.name} onConfirm={handleRename} />);
        },
        [asset.name, showDialog, handleRename]
    );

    const openMoveDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            showDialog(
                'move-asset',
                <MoveToCategoryDialog currentCategoryId={asset.categoryID} onMove={handleMove} onClose={() => closeDialog('move-asset')} />
            );
        },
        [asset.categoryID, showDialog, handleMove, closeDialog]
    );

    const openDeleteDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            showDialog(
                'delete-asset',
                <QuestionDialog
                    message={stringFormat(T._('Are you sure you want to delete "{0}"? This action cannot be undone.', 'Admin'), asset.name)}
                    onYesClick={handleDelete}
                />
            );
        },
        [asset.name, showDialog, handleDelete]
    );

    const openInfoDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            showDialog('asset-info', <AssetInfoDialog metadata={asset.metadata} assetName={asset.name} onClose={() => closeDialog('asset-info')} />);
        },
        [asset.metadata, asset.name, showDialog, closeDialog]
    );

    const handleUseAsInput = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (onUseAsInput) {
                onUseAsInput(asset.fileUrl);
            }
        },
        [asset.fileUrl, onUseAsInput]
    );

    const handleExpand = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onExpand?.();
        },
        [onExpand]
    );

    const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    const handleCheckboxChange = useCallback(() => {
        if (onToggleSelect) {
            onToggleSelect(asset.id);
        }
    }, [asset.id, onToggleSelect]);

    const handleMenuToggle = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (isOpen) {
                setClose();
                onMenuClose?.();
            } else {
                onMenuOpen?.(asset.id);
                setOpen(e.currentTarget as HTMLElement, 'top', 'left');
            }
        },
        [isOpen, setOpen, setClose, onMenuOpen, onMenuClose, asset.id]
    );

    return (
        <Card>
            <AssetPreview>
                {asset.assetType === 'video' ? (
                    <>
                        <AssetVideo src={asset.fileUrl} muted loop />
                        <VideoOverlay>
                            <VideoIcon />
                            {asset.durationSeconds ? `${Math.round(asset.durationSeconds)}s` : 'Video'}
                        </VideoOverlay>
                    </>
                ) : isModelAsset(asset) ? (
                    <>
                        <Suspense fallback={null}>
                            <GlbViewer url={asset.fileUrl} />
                        </Suspense>
                        <VideoOverlay>
                            <CubeIcon />
                            {'3D'}
                        </VideoOverlay>
                    </>
                ) : (
                    <AssetImage src={asset.thumbnailUrl || asset.fileUrl} alt={asset.name} />
                )}

                {onToggleSelect && (
                    <CheckboxWrapper onClick={handleCheckboxClick}>
                        <Checkbox checked={!!isSelected} onChange={handleCheckboxChange} />
                    </CheckboxWrapper>
                )}

                <MenuButtonWrapper>
                    <ActionButton onClick={handleMenuToggle}>
                        <EllipsisVIcon />
                    </ActionButton>
                </MenuButtonWrapper>

                {isOpen && (
                    <Dropdown>
                        {onUseAsInput && asset.assetType !== 'video' && (
                            <>
                                <DropdownMenuItem onClick={handleUseAsInput}>
                                    <ArrowRightIcon />
                                    {T._('Use as Input', 'Admin')}
                                </DropdownMenuItem>
                                <DropdownDivider />
                            </>
                        )}
                        <DropdownMenuItem onClick={handleDownload}>
                            <DownloadIcon />
                            {T._('Download', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={openInfoDialog}>
                            <InfoCircleIcon />
                            {T._('Show Info', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={openRenameDialog}>
                            <EditIcon />
                            {T._('Rename', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={openMoveDialog}>
                            <FolderIcon />
                            {T._('Move to Category', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownDivider />
                        <DropdownMenuItem $danger onClick={openDeleteDialog}>
                            <TrashIcon />
                            {T._('Delete', 'Admin')}
                        </DropdownMenuItem>
                    </Dropdown>
                )}

                <ZTooltip
                    position='bottom'
                    title={asset.assetType === 'video' ? T._('Play Video', 'Admin') : T._('View Full Size', 'Admin')}
                    enterDelay={0}
                    exitDelay={0}
                    interactive={false}
                >
                    <ExpandButton onClick={handleExpand}>{asset.assetType === 'video' ? <PlayIcon /> : <ExpandIcon />}</ExpandButton>
                </ZTooltip>
            </AssetPreview>

            <ZTooltip position='bottom' title={asset.name} enterDelay={0} exitDelay={0} interactive={false}>
                <AssetInfo>
                    <AssetName>{asset.name}</AssetName>
                    <AssetMeta>
                        {asset.width && asset.height && (
                            <span>
                                {asset.width} × {asset.height}
                            </span>
                        )}
                        {asset.categoryName && (
                            <CategoryBadge>
                                <FolderIcon />
                                {asset.categoryName}
                            </CategoryBadge>
                        )}
                    </AssetMeta>
                </AssetInfo>
            </ZTooltip>
        </Card>
    );
};

/* ─── LocalAssetItem — same visual style, no backend mutations ──────────── */

export interface LocalModel3DItem {
    id: string;
    label: string;
    toolId: string;
    thumbnailUrl?: string;
    modelUrl?: string;
    prompt?: string;
    createdAt: number;
}

const Local3DBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 6px;
    background: rgba(124, 58, 237, 0.1);
    color: #7c3aed;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
`;

const LocalPlaceholder = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0ebff;
`;

interface LocalAssetItemProps {
    item: LocalModel3DItem;
    onRename?: (id: string, newName: string) => void;
    onOpenIn3D?: (item: LocalModel3DItem) => void;
}

export const LocalAssetItem: FC<LocalAssetItemProps> = ({ item, onRename, onOpenIn3D }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [setOpen, setClose, isOpen, Dropdown] = useDropdown();

    const handleDownload = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            setClose();
            if (!item.thumbnailUrl) return;
            try {
                await downloadFile(item.thumbnailUrl, `ai-3d-model-${item.label}.png`);
            } catch (error) {
                console.error('Failed to download file:', error);
            }
        },
        [item, setClose]
    );

    const handleShowInfo = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setClose();
            showDialog('local-asset-info', <Local3DInfoDialog item={item} onClose={() => closeDialog('local-asset-info')} />);
        },
        [item, showDialog, closeDialog, setClose]
    );

    const handleRename = useCallback(
        (newName: string) => {
            if (!newName.trim()) {
                showErrorNotification({ message: T._('Type a valid name', 'Admin') });
                return;
            }
            onRename?.(item.id, newName.trim());
            closeDialog('rename-local-asset');
            showSuccessNotification({ message: T._('Asset renamed.', 'Admin') });
        },
        [item.id, onRename, closeDialog]
    );

    const openRenameDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setClose();
            showDialog('rename-local-asset', <InputDialog label={T._('Asset Name', 'Admin')} defaultValue={item.label} onConfirm={handleRename} />);
        },
        [item.label, showDialog, handleRename, setClose]
    );

    const openMoveDialog = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setClose();
            showDialog(
                'move-local-asset',
                <MoveToCategoryDialog
                    currentCategoryId={null}
                    onMove={() => {
                        closeDialog('move-local-asset');
                        showSuccessNotification({ message: T._('Asset moved.', 'Admin') });
                    }}
                    onClose={() => closeDialog('move-local-asset')}
                />
            );
        },
        [showDialog, closeDialog, setClose]
    );

    const handleOpenIn3D = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onOpenIn3D?.(item);
        },
        [item, onOpenIn3D]
    );

    const handleMenuToggle = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (isOpen) setClose();
            else setOpen(e.currentTarget as HTMLElement, 'top', 'left');
        },
        [isOpen, setOpen, setClose]
    );

    const [thumbnailBroken, setThumbnailBroken] = React.useState(false);
    React.useEffect(() => {
        setThumbnailBroken(false);
    }, [item.thumbnailUrl]);

    const LocalPreviewPlaceholder = () => (
        <LocalPlaceholder>
            <svg width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='#a855f7' strokeWidth='1.5'>
                <path d='M12 3L2 7.5 12 12 22 7.5 12 3z' />
                <path d='M2 12l10 4.5L22 12' />
                <path d='M2 16.5l10 4.5 10-4.5' />
            </svg>
        </LocalPlaceholder>
    );

    return (
        <Card>
            <AssetPreview>
                {item.thumbnailUrl && !thumbnailBroken ? (
                    <AssetImage src={item.thumbnailUrl} alt={item.label} onError={() => setThumbnailBroken(true)} />
                ) : (
                    <LocalPreviewPlaceholder />
                )}

                <MenuButtonWrapper>
                    <ActionButton onClick={handleMenuToggle}>
                        <EllipsisVIcon />
                    </ActionButton>
                </MenuButtonWrapper>

                {isOpen && (
                    <Dropdown>
                        <DropdownMenuItem onClick={handleDownload}>
                            <DownloadIcon />
                            {T._('Download', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleShowInfo}>
                            <InfoCircleIcon />
                            {T._('Show Info', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={openRenameDialog}>
                            <EditIcon />
                            {T._('Rename', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={openMoveDialog}>
                            <FolderIcon />
                            {T._('Move to Category', 'Admin')}
                        </DropdownMenuItem>
                    </Dropdown>
                )}

                <ZTooltip position='bottom' title={T._('Preview 3D', 'Admin')} enterDelay={0} exitDelay={0} interactive={false}>
                    <ExpandButton onClick={handleOpenIn3D}>
                        <ExpandIcon />
                    </ExpandButton>
                </ZTooltip>
            </AssetPreview>
            <AssetInfo>
                <AssetName>{item.label}</AssetName>
                <AssetMeta>
                    <Local3DBadge>3D</Local3DBadge>
                </AssetMeta>
            </AssetInfo>
        </Card>
    );
};
